/* eslint-disable
  no-param-reassign,
  no-underscore-dangle,
  no-console,
  class-methods-use-this,
*/
import './coa.d';
import { validate } from 'uuid';
import Cookie from 'js-cookie';
import {
  addHours,
  isAfter,
  parseISO,
} from 'date-fns';
import {
  get,
  post,
  put,
  del,
} from '../http';
import {
  getAmpersendParams,
  getDestinationURLParam,
  getAllAmpersendParams,
} from '../mymove';
import { isLocalDev } from '../environment';

const coaUUIDFromURL = new URL(window.location)?.pathname?.split('/')?.pop();
const parsedUUID = validate(coaUUIDFromURL) ? coaUUIDFromURL : '';

const notAuthenticatedError = new Error('api token is expired, or not present. unable to get a new one');
const notAuthenticatedErrorPromise = Promise.resolve(notAuthenticatedError);

/**
 * An API layer used to kick off MGO API requests
 */
export default class COA {
  /**
   * @param {String} api - the fully qualified domain to issue requests against
   * @param {String} origin - used for coa creation and source identity for EMF
   * @param {String} postbackURL
   * @param {String} coaUUID - The uuid
   */
  constructor({
    api = null,
    origin = 'MGO',
    postbackURL = '',
    coaUUID = '',
    sid = '',
    orderId = null,
  }) {
    /**
     * The fully qualified base url for the MGO API
     * @type {String}
     */
    this.api = api;

    /**
     * Used during the Global Payment handoff to redirect on success
     * @type {String}
     */
    this.postbackURL = postbackURL;

    /**
     * Used to identify the application communicating with the API
     * @type {String}
     */
    this.origin = origin;

    /**
     * The current COA in progress
     * @type {String}
     */
    this.coaUUID = coaUUID;

    /**
     * The current COA in progress orderId
     * @type {Number}
     */
    this.orderId = orderId;
    /**
     * The Visit Id of the current COA
     * Hydrated on Get and Create
     * @type {String}
     */
    this.visitId = '';
    /**
     * Used to authorize requests against the MGO API
     * @type {String}
     */
    this.token = '';

    /**
     * sid is the session id used to join many coas to one user
     * @type {String}
     */
    this.sid = sid;
  }

  /**
   * Determines if the API is authenticated
   * @return {Boolean}
   */
  _isAuthenticated() {
    this._restoreCachedToken();
    return !!this.token;
  }

  /**
   * Used to Authorize the MGO API for all subsequent requests
   * Will set the class instance token on success
   * @return {Promise<Boolean>)}
   */
  async _getAuthToken(clientId = 'mgo-app') {
    if (this._isAuthenticated()) {
      return Promise.resolve(true);
    }

    const { access_token: token } = await post(`${this.api}/mgo-auth/oauth/token`, {}, {
      params: {
        grant_type: 'client_credentials',
        client_id: clientId,
      },
    });

    this.token = token || '';
    this._setTokenCache(this.token, addHours(new Date(), 1));

    if (!this.token) {
      console.error(new Error('could not authenticate api'));
    }

    return Promise.resolve(!!this.token);
  }

  _setTokenCache(token, expiresOn) {
    Cookie.set('apit', {
      token,
      expiresOn,
    },
    {
      sameSite: 'strict',
      secure: !isLocalDev(window.location.origin),
    });
  }

  _restoreCachedToken() {
    const cache = Cookie.get('apit') || '{}';
    const { token, expiresOn } = JSON.parse(cache);
    if (expiresOn && isAfter(new Date(), parseISO(expiresOn))) {
      this.token = '';
      return;
    }

    if (token) {
      this.token = token;
    }
  }

  reset() {
    this.coaUUID = '';
    this.visitId = '';
  }

  /**
   * Retrieve Application Configuration Details
   * These config items relate to Command Center
   * @method configGet
   * @return {Promise<Config>}
   */
  async configGet() {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return get(`${this.api}/mgo-api/api/v1/app/config`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Retreive updated ICOA amount based on COA data
   * @return {Promise<String>}
   */
  async icoaAmountGet() {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return get(`${this.api}/mgo-api/api/v1/payment/${this.coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Verify google recaptcha v3
   * https://developers.google.com/recaptcha/docs/v3#programmatically_invoke_the_challenge
   * @param {String} responseToken - token from recaptcha V3
   * @param {String} action - the recaptcha action
   * @return {Promise}
   */
  async recaptchaVerify(responseToken, action) {
    return post(`${this.api}/mgo-api/api/v1/rc`, {
      orderUuid: this.coaUUID,
      action,
      responseToken,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Get Global Payment forwarding Information
   * @param {String} returnUrl - The url to return a user to if they use GP Back
   * @param {String} suffixUrl - This is a string that is attached to the end of the url
   * @return {Promise<GlobalPaymentResponse>}
   */
  async globalPaymentURLGet(returnUrl, suffixUrl = '') {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return post(`${this.api}/mgo-api/api/v1/payment`, {
      orderUuid: this.coaUUID,
      postbackUrl: `${this.postbackURL}?coa_uuid=${this.coaUUID}${suffixUrl}`,
      returnUrl,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Determine if the COA is DCOA or not
   * @return {Promise<DCOA>}
   */
  async isDCOA() {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return get(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Check COA to see if there's exceptions
   * Used on ICOA flow - MoveCombined/Disclaimer, VerifyInfoEdit
   * @return {Promise}
   */
  async coaCheckByUUID(coaUUID) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return get(
      `${this.api}/mgo-api/api/v1/coa/${coaUUID}/check`, {}, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      },
    );
  }

  /**
   * Check COA to see if there's exceptions
   * Used on ICOA flow - MoveCombined/Disclaimer, VerifyInfoEdit
   * @return {Promise}
   */
  async coaCheck() {
    return this.coaCheckByUUID(this.coaUUID);
  }

  /**
   * Verify COA using DOB
   * Used in DCOA flow
   * @param {String} birthDate - Formatted as yyyy-MM-dd
   * @return {Promise<String>}
   */
  async verifyAge(birthDate) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return post(`${this.api}/mgo-api/api/v1/identity/id-verification/${this.coaUUID}?birthdate=${birthDate}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Verify Business DCOA
   * @return {Promise<String>}
   */
  async verifyBusinessDCOA(simulate = false) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return post(`${this.api}/mgo-api/api/v1/identity/id-verification/${this.coaUUID}${simulate ? '/simulate' : ''}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }
  /**
   * COA Object Actions
   * ----------------------------------------------------------------
   */

  /**
    * Create the COA empty
    * This allows us to issue patches to the COA as information
    * becomes available
    * @param {String} utmMedium - utm_medium query param
    * @return {Promise<Boolean>}
    */
  async coaCreateNew(utmMedium = '') {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    const getChannel = () => {
      if (this.singleVal(utmMedium) === 'cpc') return 'paid';
      if (utmMedium) return this.singleVal(utmMedium);
      return 'organic';
    };

    return post(`${this.api}/mgo-api/api/v1/coa`, {
      origin: this.origin,
      sid: this.sid,
      channel: getChannel(),
      uuid: this.coaUUID, // uuid of the primary COA
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }
  /**
   * COA Object Actions
   * ----------------------------------------------------------------
   */

  /**
    * Create the COA empty
    * This allows us to issue patches to the COA as information
    * becomes available
    * @param {String} utmMedium - utm_medium query param
    * @return {Promise<Boolean>}
    */
  async coaCreate(utmMedium = '') {
    if (this.coaUUID) {
      return Promise.resolve(true);
    }

    const { uuid, visitId, orderId } = await this.coaCreateNew(utmMedium);

    this.coaUUID = uuid || '';
    this.orderId = orderId || null;
    if (!this.visitId) this.visitId = visitId || '';

    if (!this.coaUUID) {
      console.log(new Error('unable to create coa'));
    }

    return Promise.resolve(!!uuid);
  }

  /**
   * Patch the COA as information is available
   * @param {Boolean} informedDelivery - User registered for ID
   * @param {String} moverType - INDIVIDUAL | FAMILY | PERMANENT
   * @param {String} forwardType - PERMANENT | TEMPORARY
   * @param {String} startDate - The start date of forwarding - yyyy-MM-dd
   * @param {String} stopDate - If the user is temporary, the end date of forwarding - yyyy-MM-dd
   * @param {String} billingAddressType - OLD | NEW | OTHER
   * @param {String} businessName - when forardType is BUSINESS
   * @param {String} email - email for reminders or confirmations
   * @return {Promise<Coa>}
   */
  async coaUpdate({
    /* eslint-disable */
    informedDelivery = false,
    moverType = null,
    forwardType = null,
    startDate = null,
    stopDate = null,
    billingAddressType = null,
    businessName = null,
    email = null,
    famPrompt1ShareLastName = null,
    famPrompt2SameLastNameRemaining = null,
    bizPromptEntireMoving = null,
    sdBuffered=false,
    earlyOptin = '0',
    multipleAddresses = false,
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return put(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}`, {
      origin: this.origin,
      uuid: this.coaUUID,
      informedDelivery,
      moverType,
      forwardType,
      startDate,
      stopDate,
      billingAddressType,
      businessName,
      email,
      famPrompt1ShareLastName,
      famPrompt2SameLastNameRemaining,
      bizPromptEntireMoving,
      sdBuffered,
      earlyOptin: parseInt(earlyOptin),
      multipleAddresses,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

   /**
   * Patch the Business COA as information is available
   * @param {String} orderId - order Id of business COA being update
   * @param {Boolean} informedDelivery - User registered for ID
   * @param {String} moverType - INDIVIDUAL | FAMILY | PERMANENT
   * @param {String} forwardType - PERMANENT | TEMPORARY
   * @param {String} startDate - The start date of forwarding - yyyy-MM-dd
   * @param {String} stopDate - If the user is temporary, the end date of forwarding - yyyy-MM-dd
   * @param {String} billingAddressType - OLD | NEW | OTHER
   * @param {String} businessName - when forardType is BUSINESS
   * @param {String} email - email for reminders or confirmations
   * @return {Promise<Coa>}
   */
   async businessCoaUpdate({
    /* eslint-disable */
    orderId = '',
    multipleAddresses = false,
    informedDelivery = false,
    moverType = null,
    forwardType = null,
    startDate = null,
    stopDate = null,
    billingAddressType = null,
    businessName = null,
    email = null,
    famPrompt1ShareLastName = null,
    famPrompt2SameLastNameRemaining = null,
    bizPromptEntireMoving = null,
    sdBuffered=false,
    earlyOptin = '0',
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return put(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/business/${orderId}`, {
      origin: this.origin,
      multipleAddresses,
      uuid: this.coaUUID,
      informedDelivery,
      moverType,
      forwardType,
      startDate,
      stopDate,
      billingAddressType,
      businessName,
      email,
      famPrompt1ShareLastName,
      famPrompt2SameLastNameRemaining,
      bizPromptEntireMoving,
      sdBuffered,
      earlyOptin: parseInt(earlyOptin),
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Delete a business COA for multiple businesses
   * @param {String} orderId - order Id of business COA being update
   * @return {Promise<Coa>}
   */
  async businessDeleteCoa(orderId) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return await del(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/business/${orderId}`, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      }
    })
  }


  /**
   * Patch the COA as information is available - Preliminary COA update call for simplify
   * @param {String} moverType - INDIVIDUAL | FAMILY | PERMANENT
   * @return {Promise<Coa>}
   */
  async coaUpdateV2({
    /* eslint-disable */
    moverType = null,
    famPrompt1ShareLastName = null,
    famPrompt2SameLastNameRemaining = null,
    bizPromptEntireMoving = null,
    multipleAddresses = false
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return put(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/partial`, {
      origin: this.origin,
      uuid: this.coaUUID, 
      moverType,
      famPrompt1ShareLastName,
      famPrompt2SameLastNameRemaining,
      bizPromptEntireMoving,
      multipleAddresses,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }
     /**
   * Patch the Business COA as information is available
   * @param {String} orderId - order Id of business COA being update
   * @param {String} moverType - INDIVIDUAL | FAMILY | BUSINESS
   * @param {String} businessName - when forardType is BUSINESS
   * @param {String} multipleAddresses - true for multiple business COA
   * @return {Promise<Coa>}
   */
     async businessPartialCoaUpdate({
      /* eslint-disable */
      orderId = '', 
      multipleAddresses = false,
      moverType = null,
      businessName = null,
      famPrompt1ShareLastName = null,
      famPrompt2SameLastNameRemaining = null,
      bizPromptEntireMoving = null,
    }) {
      await this._getAuthToken();
      if (!this._isAuthenticated()) {
        return notAuthenticatedErrorPromise;
      }
  
      return put(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/business/${orderId}/partial`, {
        origin: this.origin,
        multipleAddresses,
        uuid: this.coaUUID,
        moverType,
        businessName,
        famPrompt1ShareLastName,
        famPrompt2SameLastNameRemaining,
        bizPromptEntireMoving,
      }, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
    }
  /**
   * Retrieve the COA data
   * Used for rehydrating the user experience
   * and rendering next page information
   * @return {Promise<{visitId, Coa, moverType: string, forwardType:string}>}
   */
  async coaGet() {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    const { visitId, ...coa } = await get(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    this.visitId = visitId;

    return { visitId, ...coa, moverType: coa.moverType || '',forwardType: coa.forwardType || '' };
  }

  /**
   * Used to finalize a COA
   * after it has been verified
   * @param {String} orderNumber - Order Number used in GP
   * @param {String} cuid - Used in DCOA Verification
   * @return {Promise<String>}
   */
  async coaCommit({
    coaUUID = '',
    orderNumber = null,
    cuid = null,
    hasMultipleBusiness = false
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }
    let url = `${this.api}/mgo-api/api/v2/coa/${coaUUID}/commit`;

    if (hasMultipleBusiness) {
      url = `${this.api}/mgo-api/api/v2/coa/${coaUUID}/commit/business`;
    }
    const item = url.split('?');
    const outUrl = url?.replace(`?${item[1]}`, `/commit?${item[1]?.replace('/commit', '')}`);

    const { cfpsId } = await post(`${outUrl}`, {
      orderNumber,
      cuid,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    if( cfpsId && 
      ( JSON.parse(sessionStorage.getItem('optIntoIPP'))
      || JSON.parse(sessionStorage.getItem('passedIvsVerification'))) ){
        await this.mfaOrderStatus({ coaUUID, cfpsId, 
          orderStatus: JSON.parse(sessionStorage.getItem('passedIvsVerification')) ? 0 : 1, 
          token: this.token });
    }
    return cfpsId;
  }

  /**
   * Called to opt COA into in-person proofing
   * @param {*} orderStatus - 0 when ivs verification was successful. 1 if it failed 
   */
  async mfaOrderStatus({ coaUUID, cfpsId, orderStatus, token }) {
    const { xhr, raw } = await post(`${this.api}/mgo-api/api/v1/mfa/orderstatus`, { orderUuid: coaUUID, orderStatus, cfpsId }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const {
      exception, message,
    } = xhr || { exception: null, message: null };

    const { status } = raw || { status: null };

    if (exception || status === 500 ) {
      sessionStorage.setItem('ippRegistrationSuccess', false); 
    } else {
      sessionStorage.setItem('ippRegistrationSuccess', true); 
    }
  }
  /**
   * Mark a Military COA, confirmed
   * @property {COA} coa - The coa data from the flow
   * @return {Promize}
   */
  async confirmMCOA(coa) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return post(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/mcoa-confirm`, coa, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      }
    });
  }

  /**
   * Handle COA ID signup on email return
   * @return {Promise}
   */
  async coaInformedDeliverySignup() {
    await this._getAuthToken();
    if (!this._isAuthenticated) {
      return notAuthenticatedErrorPromise;
    }

    return post(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/infdel`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      }
    });
  }

  /**
   * Address Object Actions
   * ----------------------------------------------------------------
   */

  /**
    * Save an Address Object to a COA
    * @param {String} addressType - OLD | NEW | BILLING
    * @param {String} streetAddress
    * @param {String} city
    * @param {String} state
    * @param {String} zipCode
    * @param {String} urbanizationName - Only Used for Puerto Rico
    * @param {String} poBoxNumber
    * @param {String} privateMailbox
    * @param {Boolean} overrideAms
    * @param {Boolean} recentConstruction
    * @param {String} moverType
    * @param {String} forwardType
    * @return {Promise<Address>}
    */
  
   async addressSaveV2({
    streetAddress: oldStreetAddress = '',
    city: oldCity = '',
    state: oldState = '',
    zipCode: oldZipCode = '',
    urbanizationName: oldUrbanizationName = null,
    poBoxNumber: oldPoBoxNumber = null,
    privateMailbox : oldPrivateMailbox = null,
    overrideAms : oldOverrideAms= false,
    recentConstruction : oldRecentConstruction = false,
    moverType: oldMoverType = '',
    forwardType : oldForwardType = '',
  }, {
    streetAddress: newStreetAddress = '',
    city: newCity = '',
    state: newState = '',
    zipCode: newZipCode = '',
    urbanizationName: newUrbanizationName = null,
    poBoxNumber: newPoBoxNumber = null,
    privateMailbox : newPrivateMailbox = null,
    overrideAms : newOverrideAms= false,
    recentConstruction : newRecentConstruction = false,
    moverType: newMoverType = '',
    forwardType : newForwardType = '',
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }
    sessionStorage.setItem('coaUUID', this.coaUUID);
    return post(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/addresses`, {
      oldAddress: {
        orderUuid: this.coaUUID,
        addressType: "OLD",
        streetAddress: oldStreetAddress,
        city: oldCity,
        state: oldState,
        zipCode: oldZipCode,
        urbanizationName: oldUrbanizationName,
        poBoxNumber: oldPoBoxNumber,
        privateMailbox : oldPrivateMailbox,
        overrideAms : oldOverrideAms,
        recentConstruction : oldRecentConstruction,
        moverType: oldMoverType,
        forwardType : oldForwardType,
      },
      newAddress: {
        orderUuid: this.coaUUID,
        addressType: "NEW",
        streetAddress: newStreetAddress,
        city: newCity,
        state: newState,
        zipCode: newZipCode,
        urbanizationName: newUrbanizationName,
        poBoxNumber: newPoBoxNumber,
        privateMailbox : newPrivateMailbox,
        overrideAms : newOverrideAms,
        recentConstruction : newRecentConstruction,
        moverType: newMoverType,
        forwardType : newForwardType,
      }
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    }); 
  }

  /**
   * Address Object Actions Simplify
   * ----------------------------------------------------------------
   */

  /**
    * Save an Address Object to a COA
    * @param {String} addressType - OLD | NEW | BILLING
    * @param {String} streetAddress
    * @param {String} city
    * @param {String} state
    * @param {String} zipCode
    * @param {String} urbanizationName - Only Used for Puerto Rico
    * @param {String} poBoxNumber
    * @param {String} privateMailbox
    * @param {Boolean} overrideAms
    * @param {Boolean} recentConstruction
    * @param {String} moverType
    * @return {Promise<Address>}
    */
  
  async addressSaveV2Simplify({
    streetAddress: oldStreetAddress = '',
    city: oldCity = '',
    state: oldState = '',
    zipCode: oldZipCode = '',
    urbanizationName: oldUrbanizationName = null,
    poBoxNumber: oldPoBoxNumber = null,
    privateMailbox : oldPrivateMailbox = null,
    overrideAms : oldOverrideAms= false,
    recentConstruction : oldRecentConstruction = false,
    moverType: oldMoverType = '',
  }, {
    streetAddress: newStreetAddress = '',
    city: newCity = '',
    state: newState = '',
    zipCode: newZipCode = '',
    urbanizationName: newUrbanizationName = null,
    poBoxNumber: newPoBoxNumber = null,
    privateMailbox : newPrivateMailbox = null,
    overrideAms : newOverrideAms= false,
    recentConstruction : newRecentConstruction = false,
    moverType: newMoverType = '',
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }
    sessionStorage.setItem('coaUUID', this.coaUUID);
    return post(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/addresses`, {
      oldAddress: {
        orderUuid: this.coaUUID,
        addressType: "OLD",
        streetAddress: oldStreetAddress,
        city: oldCity,
        state: oldState,
        zipCode: oldZipCode,
        urbanizationName: oldUrbanizationName,
        poBoxNumber: oldPoBoxNumber,
        privateMailbox : oldPrivateMailbox,
        overrideAms : oldOverrideAms,
        recentConstruction : oldRecentConstruction,
        moverType: oldMoverType,
      },
      newAddress: {
        orderUuid: this.coaUUID,
        addressType: "NEW",
        streetAddress: newStreetAddress,
        city: newCity,
        state: newState,
        zipCode: newZipCode,
        urbanizationName: newUrbanizationName,
        poBoxNumber: newPoBoxNumber,
        privateMailbox : newPrivateMailbox,
        overrideAms : newOverrideAms,
        recentConstruction : newRecentConstruction,
        moverType: newMoverType,
      }
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    }); 
  }

  /**
   * Business Address Object Actions
   * ----------------------------------------------------------------
   */

  /**
    * Save an Address Object to a COA
    * @param {String} orderId
    * @param {String} addressType - OLD | NEW | BILLING
    * @param {String} streetAddress
    * @param {String} city
    * @param {String} state
    * @param {String} zipCode
    * @param {String} urbanizationName - Only Used for Puerto Rico
    * @param {String} poBoxNumber
    * @param {String} privateMailbox
    * @param {Boolean} overrideAms
    * @param {Boolean} recentConstruction
    * @param {String} moverType
    * @param {String} forwardType
    * @return {Promise<Address>}
    */
  
   async businessAddressSave(orderId,{
    streetAddress: oldStreetAddress = '',
    city: oldCity = '',
    state: oldState = '',
    zipCode: oldZipCode = '',
    urbanizationName: oldUrbanizationName = null,
    poBoxNumber: oldPoBoxNumber = null,
    privateMailbox : oldPrivateMailbox = null,
    overrideAms : oldOverrideAms= false,
    recentConstruction : oldRecentConstruction = false,
    moverType: oldMoverType = '',
    forwardType: oldForwardType = '',
  }, {
    streetAddress: newStreetAddress = '',
    city: newCity = '',
    state: newState = '',
    zipCode: newZipCode = '',
    urbanizationName: newUrbanizationName = null,
    poBoxNumber: newPoBoxNumber = null,
    privateMailbox : newPrivateMailbox = null,
    overrideAms : newOverrideAms= false,
    recentConstruction : newRecentConstruction = false,
    moverType: newMoverType = '',
    forwardType: newForwardType = ''
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }
    sessionStorage.setItem('coaUUID', this.coaUUID);
    return post(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/busaddr/${orderId}`, {
      oldAddress: {
        orderUuid: this.coaUUID,
        addressType: "OLD",
        streetAddress: oldStreetAddress,
        city: oldCity,
        state: oldState,
        zipCode: oldZipCode,
        urbanizationName: oldUrbanizationName,
        poBoxNumber: oldPoBoxNumber,
        privateMailbox : oldPrivateMailbox,
        overrideAms : oldOverrideAms,
        recentConstruction : oldRecentConstruction,
        moverType: oldMoverType,
        forwardType: oldForwardType,
      },
      newAddress: {
        orderUuid: this.coaUUID,
        addressType: "NEW",
        streetAddress: newStreetAddress,
        city: newCity,
        state: newState,
        zipCode: newZipCode,
        urbanizationName: newUrbanizationName,
        poBoxNumber: newPoBoxNumber,
        privateMailbox : newPrivateMailbox,
        overrideAms : newOverrideAms,
        recentConstruction : newRecentConstruction,
        moverType: newMoverType,
        forwardType: newForwardType,
      }
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    }); 
  }

  async businessAddressSaveSimplify(orderId,{
    streetAddress: oldStreetAddress = '',
    city: oldCity = '',
    state: oldState = '',
    zipCode: oldZipCode = '',
    urbanizationName: oldUrbanizationName = null,
    poBoxNumber: oldPoBoxNumber = null,
    privateMailbox : oldPrivateMailbox = null,
    overrideAms : oldOverrideAms= false,
    recentConstruction : oldRecentConstruction = false,
    moverType: oldMoverType = '',
  }, {
    streetAddress: newStreetAddress = '',
    city: newCity = '',
    state: newState = '',
    zipCode: newZipCode = '',
    urbanizationName: newUrbanizationName = null,
    poBoxNumber: newPoBoxNumber = null,
    privateMailbox : newPrivateMailbox = null,
    overrideAms : newOverrideAms= false,
    recentConstruction : newRecentConstruction = false,
    moverType: newMoverType = '',
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }
    sessionStorage.setItem('coaUUID', this.coaUUID);
    return post(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/busaddr/${orderId}`, {
      oldAddress: {
        orderUuid: this.coaUUID,
        addressType: "OLD",
        streetAddress: oldStreetAddress,
        city: oldCity,
        state: oldState,
        zipCode: oldZipCode,
        urbanizationName: oldUrbanizationName,
        poBoxNumber: oldPoBoxNumber,
        privateMailbox : oldPrivateMailbox,
        overrideAms : oldOverrideAms,
        recentConstruction : oldRecentConstruction,
        moverType: oldMoverType,
      },
      newAddress: {
        orderUuid: this.coaUUID,
        addressType: "NEW",
        streetAddress: newStreetAddress,
        city: newCity,
        state: newState,
        zipCode: newZipCode,
        urbanizationName: newUrbanizationName,
        poBoxNumber: newPoBoxNumber,
        privateMailbox : newPrivateMailbox,
        overrideAms : newOverrideAms,
        recentConstruction : newRecentConstruction,
        moverType: newMoverType,
      }
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    }); 
  }

  async businessBillingAddressSave(orderId,{
    streetAddress: billingStreetAddress = '',
    city: billingCity = '',
    state: billingState = '',
    zipCode: billingZipCode = '',
    urbanizationName: billingUrbanizationName = null,
    poBoxNumber: billingPoBoxNumber = null,
    privateMailbox: billingPrivateMailbox = null,
    overrideAms: billingOverrideAms = false,
    recentConstruction: billingRecentConstruction = false,
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }
    return post(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/busaddr/${orderId}`, {
      billingAddress: {
        orderUuid: this.coaUUID,
        addressType: "BILLING",
        streetAddress: billingStreetAddress,
        city: billingCity,
        state: billingState,
        zipCode: billingZipCode,
        urbanizationName: billingUrbanizationName,
        poBoxNumber: billingPoBoxNumber,
        privateMailbox: billingPrivateMailbox,
        overrideAms: billingOverrideAms,
        recentConstruction: billingRecentConstruction,
      },
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  async businessBillingAddressDelete() {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }
    return del(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/busaddr`, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  async addressSave({
    addressType = 'OLD',
    streetAddress = '',
    city = '',
    state = '',
    zipCode = '',
    urbanizationName = null,
    poBoxNumber = null,
    privateMailbox = null,
    overrideAms = false,
    recentConstruction = false,
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return post(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/addresses`, {
      orderUuid: this.coaUUID,
      addressType,
      streetAddress,
      city,
      state,
      zipCode,
      urbanizationName,
      poBoxNumber,
      privateMailbox,
      overrideAms,
      recentConstruction,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    }); 
  }

  async addressDelete(addressUUID) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }
    return await fetch(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/addresses/${addressUUID}`, {
      mode: 'cors',
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${this.token}`,
      }
    })
  }


  /**
   * Retreive all Address associated to a COA
   * @return {Promise<Address[]>}
   */
  async addressGetAll() {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    const response = await get(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/addresses`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    if (Array.isArray(response)) {
      return { addresses: response };
    }

    return { ...response, addresses: [] };
  }

    /**
   * Retreive all Business Addresses associated to a COA
   * @return {Promise<Address[]>}
   */
    async busAddrGetAll() {
      await this._getAuthToken();
      if (!this._isAuthenticated()) {
        return notAuthenticatedErrorPromise;
      }
  
      const response = await get(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/busaddr`, {}, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
  
      if (Array.isArray(response)) {
        return { addresses: response };
      }
  
      return { ...response, addresses: [] };
    }

  /**
   * Retreive all Address associated to a COA
   * @return {Promise<{thirdAddressEligible: boolean, oldAddress: Address, newAddress: Address, thirdAddress: Address}>}
   */
  async addressGetAllV2() {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return get(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/addresses`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * This is used to log exceptions created by invalid address data
   * @param {String} exception - The API response exception
   * @param {Object} oldAddress - basic old address properties
   * @param {Object} newAddress - basic new address properties
   * @return {Promise}
   */
  async addressException(exception, {
    streetAddress: oldStreetAddress = null,
    city: oldCity = null,
    state: oldState = null,
    zipCode: oldZipCode = null,
  }, {
    streetAddress: newStreetAddress = null,
    city: newCity = null,
    state: newState = null,
    zipCode: newZipCode = null,
  }, {
    streetAddress: thirdStreetAddress = null,
    city: thirdCity = null,
    state: thirdState = null,
    zipCode: thirdZipCode = null,
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }
    if (exception !== "EXACT_MATCH") {
      return post(`${this.api}/mgo-api/api/v1/exception/${this.coaUUID}`, {
        exception,
        oldAddress: {
          streetAddress: oldStreetAddress,
          city: oldCity,
          state: oldState,
          zipCode: oldZipCode,
        },
        newAddress: {
          streetAddress: newStreetAddress,
          city: newCity,
          state: newState,
          zipCode: newZipCode,
        },
        thirdAddress: {
          streetAddress: thirdStreetAddress,
          city: thirdCity,
          state: thirdState,
          zipCode: thirdZipCode,
        },
      }, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
      }
      return post(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/log-validation`, {}, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });
  }

  /**
   * Person Object Actions
   * ----------------------------------------------------------------
   */

  /**
    * Saves a Person object to a COA
    * @param {String} personType - MOVER | PREPARER
    * @param {String} firstName
    * @param {String} middleName
    * @param {String} lastName
    * @param {String} email
    * @param {String} mobilePhone - Required if homePhone not present
    * @param {String} homePhone - Required if mobilePhone not present
    * @return {Promise<Person>}
    */
  async personSave({
    personType = 'MOVER',
    firstName = '',
    middleName = '',
    lastName = '',
    email = '',
    ...params
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return post(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/persons`, {
      orderUuid: this.coaUUID,
      personType,
      firstName,
      middleName,
      lastName,
      email,
      emailConfirm: email,
      ...params, // for phone field
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Person Object Actions for multiple businesses
   * ----------------------------------------------------------------
   */

  /**
    * Saves a Person object to a COA - Updates person records for all secondary and primary
   * business
    * @param {String} personType - MOVER | PREPARER
    * @param {String} firstName
    * @param {String} middleName
    * @param {String} lastName
    * @param {String} email
    * @param {String} mobilePhone - Required if homePhone not present
    * @param {String} homePhone - Required if mobilePhone not present
    * @return {Promise<Person>}
    */
  async businessPersonSave({
    personType = 'MOVER',
    firstName = '',
    middleName = '',
    lastName = '',
    email = '',
    ...params
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return put(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/business/persons`, {
      orderUuid: this.coaUUID,
      personType,
      firstName,
      middleName,
      lastName,
      email,
      emailConfirm: email,
      ...params, // for phone field
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }
  /**
   * Person Object Actions
   * ----------------------------------------------------------------
   */

  /**
    * Saves a Person of a specific orderId object to a COA
    * @param {String} orderId
    * @param {String} personType - MOVER | PREPARER
    * @param {String} firstName
    * @param {String} middleName
    * @param {String} lastName
    * @param {String} email
    * @param {String} mobilePhone - Required if homePhone not present
    * @param {String} homePhone - Required if mobilePhone not present
    * @return {Promise<Person>}
    */
  async personSaveByOrderId(orderId,{
    personType = 'MOVER',
    firstName = '',
    middleName = '',
    lastName = '',
    email = '',
    ...params
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return post(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/persons/${orderId}`, {
      orderUuid: this.coaUUID,
      personType,
      firstName,
      middleName,
      lastName,
      email,
      emailConfirm: email,
      ...params, // for phone field
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }
    /**
   * Retreive all Person Objects on a COA
   * @return {Promise<Person[]>}
   */
  async personGetAll() {
    return this.personGetAllByUuid(this.coaUUID);
  }
  /**
   * Retreive all Person Objects on a COA
   * @return {Promise<Person[]>}
   */
  async personGetAllByUuid(coaUUID) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return await get(`${this.api}/mgo-api/api/v2/coa/${coaUUID}/persons`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

    /**
   * Retreive all Person Objects on a COA
   * @return {Promise<Person[]>}
   */
    async businessGetAll() {
      await this._getAuthToken();
      if (!this._isAuthenticated()) {
        return notAuthenticatedErrorPromise;
      }
  
      return await get(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/business`, {}, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
    }

  async personDelete() {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }
    
    return await fetch(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/persons`, {
      mode : 'cors', 
      method : 'DELETE',
      headers: {
        Authorization: `Bearer ${this.token}`,
      },

    })
  }

  async businessPersonDelete() {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }
    
    return await fetch(`${this.api}/mgo-api/api/v2/coa/${this.coaUUID}/business/persons`, {
      mode : 'cors', 
      method : 'DELETE',
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    })
  }
  
    /**
    * Determine if COA qualifies for Extended Mail Forwarding (EMF)
    * @param {String} cfpsid - required if orderUuid not present
    * @param {String} orderUuid - required if cfpsid not present
    * @param {String} forwardType - PERMANENT | TEMPORARY
    * @param {Boolean} dcoa - is Distaster COA
    * @param {Boolean} mcoa - is Military COA
    * @param {String} oldZip5
    * @param {String} newZip5
    * @return {Promise<Prescreen>}
    */
     async extensionPrescreen({
      cfpsid = '',
      orderUuid = '',
      forwardType = '',
      dcoa = false,
      mcoa = false,
      oldZip5 = '',
      newZip5 = '',
    }) {
      await this._getAuthToken();
      if (!this._isAuthenticated()) {
        return notAuthenticatedErrorPromise;
      }
      if (forwardType!== 'PERMANENT') return { eligible: false };
      const extensionList = await this.extensionGetAll(this.coa?.visitId);
      return { eligible: (extensionList?.availableExtensions?.length > 0) ? true : false };
    }

  async personGet(type = 'mover') {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return get(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/mover`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      }
    });
  }

  /**
   * EMF Actions
   * ----------------------------------------------------------------
   */
  
  /**
   * Get EMF option data
   * @return {Promise<EMFResponse>}
   */
  async extensionGetAll(visitId) {
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return get(`${this.api}/mgo-api/api/v1/extension/${this.coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * persist users emf selection
   * @param {String} optionId
   * @param {String} visitId
   * @return {Promise}
   */
  async extensionSave(optionId, visitId) {
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return post(`${this.api}/mgo-api/api/v1/extension/${this.coaUUID}?option=${optionId}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Business Alias Actions
   * ----------------------------------------------------------------
   */

  /**
    * Save Business Alias Data
    * Can be an empty list
    * @param {String[]} aliases - a list of strings representing each alias
    * @return {Promise<AliasResponse>}
    */
  async businessAliasSave([...aliases]) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return post(`${this.api}/mgo-api/api/v1/alias/${this.coaUUID}`, {
      aliases,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }
  /**
   * Save Business Alias Data
   * Can be an empty list
   * @param {String} orderId - order id uniquely identifies these list of alias in a multiple business filed by COA
   * @param {String[]} aliases - a list of strings representing each alias
   * @return {Promise<AliasResponse>}
   */
 async businessAliasSaveByOrderId(orderId, [...aliases]) {
   await this._getAuthToken();
   if (!this._isAuthenticated()) {
     return notAuthenticatedErrorPromise;
   }

   return post(`${this.api}/mgo-api/api/v1/alias/${this.coaUUID}/orderId/${orderId}`, {
     aliases,
   }, {
     headers: {
       Authorization: `Bearer ${this.token}`,
     },
   });
 }

  /**
   * Get all business alias data on a COA
   * @return {AliasResponse}
   */
  async businessAliasGetAll() {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return get(`${this.api}/mgo-api/api/v1/alias/${this.coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

    /**
   * Get all business alias by orderId data on a COA
   * @return {AliasResponse}
   */
    async businessAliasGetAllByOrderId(orderId) {
      await this._getAuthToken();
      if (!this._isAuthenticated()) {
        return notAuthenticatedErrorPromise;
      }
  
      return get(`${this.api}/mgo-api/api/v1/alias/${this.coaUUID}/orderId/${orderId}`, {}, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
    }
  
  /**
   * Email Actions
   * ----------------------------------------------------------------
   */

  /**
   * Allow the COA to opt out of email reminders
   * @return {Promise}
   */
  async emailReminderOptOut() {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return post(`${this.api}/mgo-api/api/v1/e/unsubscribe/${this.coaUUID}?type=reminder`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }
  async emailReminderOptIn() {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return post(`${this.api}/mgo-api/api/v1/event/subscribe/${this.coaUUID}?type=reminder`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Send post-commit emails
   */
  async emailSend() {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }
    if (this.coaUUID.length>0) {

      const [coaUUID, clientID] = this.coaUUID.split('?');
      this.clientID = clientID
      if(JSON.parse(sessionStorage.getItem('optIntoIPP'))) {
        return post(`${this.api}/mgo-api/api/v1/email/${coaUUID}/deals/send`, {}, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          }
        });
      } else{
        return post(`${this.api}/mgo-api/api/v1/email/${coaUUID}/send`, {}, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          }
        });
     }
    }
  }

  /**
   * Unsubscribe email
   * @param {String} coaUUID - COA order uuid
   * @param {String} ref - Email template ref
   * @param {String} jwt - token provided by unsub link query params
   * @return {Promise<String>}
   */
  async unsubscribe(coaUUID, ref, jwt = '') {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    return post(`${this.api}/mgo-api/api/v1/email/unsubscribe`, {
      jwt,
      ref,
      uuid: coaUUID,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * MYMOVE
   * ----------------------------------------------------------------
   */
  /**
   * Get Access Handoff URL
   * @param {String} dest - Access flow to send the user down (ex. 'vr' or 'offers')
   * @param {Boolean} autofill - User opted in to allow data transfer to MYMOVE
   * @param {Object} queryParamObj - Query params key value pairs used to override utm params
   * @return {Promise}
   */
  async handoffGetURL(dest, autofill,  {utm_campaign = 'default', utm_medium = 'sp',utm_source = 'usps'}) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }
    const coaUUID = window.location.pathname.split('/').pop();
    return get(`${this.api}/mgo-api/api/v1/handoffs/${coaUUID}`, {
      params: {
        ...getAmpersendParams(),
        ...getAllAmpersendParams(),
        destinationUrl: getDestinationURLParam(),
        autofill,
        dest,
        campaign: utm_campaign,
        medium: utm_medium,
        source: utm_source,
      },
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Analytics Events
   * ----------------------------------------------------------------
   */

  /**
   * Get the visitId
   * @param {String} deviceType - DESKTOP | MOBILE
   * @return {Promise<String>}
   */
  async visit(deviceType) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    const visitId = await post(
      `${this.api}/mgo-api/api/v1/e/v`,
      {
        params: {
          qs: location.search.slice(1),
          deviceType,
        },
      },
      {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      },
    );

    this.visitId = visitId || '';
  }
  /**
   * Return first value if input is an array
   * @param {*} val 
   * @returns 
   */
 singleVal(val){
    return val && Array.isArray(val) && val.length ? val[0] : val;
  }
  /**
    * Track Segment server page view event
    * @param {String} deviceType - DESKTOP | MOBILE
    * @param {String} pageId - ID from analytics route map
    * @param {String} pageName - route name
    * @param {String} adgid - utm param
    * @param {String} gclid - utm param
    * @param {String} source - utm param
    * @return {Promise<String>}
    */
  async pageView({
    deviceType,
    pageId,
    pageName,
    adgid,
    gclid,
    source,
    referral,
  }) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    // context of this is missing a token after visit is called
    const token = this.token;
    if (!this.visitId) {
      await this.visit(deviceType);
    }

    if ((this.coaUUID || this.visitId) && pageId) {
      const [coaUUID, clientID] = this.coaUUID.split('?')
      this.clientID = clientID;
      const payload = {
        sid: this.sid,
        [coaUUID ? 'uuid' : 'visitId']: coaUUID || this.visitId,
        deviceType,
        pageName,
        referral: this.singleVal(referral),
        context_campaign_adgid: this.singleVal(adgid),
        context_campaign_gclid: this.singleVal(gclid),
        context_campaign_source: this.singleVal(source),
      };

      if (pageId.toString().indexOf(',') >= 0) {
        payload.viewCode = pageId.toString();
      } else {
        payload.pageId = pageId;
      }

      return post(
        `${this.api}/mgo-api/api/v1/e/pv`,
        { ...payload },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    }

    return Promise.resolve(!!pageId)
  }

  async eventTrack(event, properties = {}) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    try {
      if (this.coaUUID) {
        await post(`${this.api}/mgo-api/api/v1/e/${this.coaUUID}`,
          { event, properties, },
          {
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
          },
        );
      } else {
        setTimeout(()=> {this.eventTrack(event, properties)}, 1000);
      }
    } catch (error) {
      this.$store.dispatch('setDevNull', error);
    }

    return Promise.resolve(true);
  }

  /**
   * Track engagement event
   * @param {String} refId - event reference id
   * @param {Array} offerIds - ids of offers to track
   * @return {Promise}
   */
  async trackEngagement(refId, offerIds) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    const events = {
      110: {
        events: 'event110',
        name: 'See Details Page - Dedicated Email',
        products: offerIds,
        refId,
        properties: { evar6: '' },
      },
      111: {
        events: 'event111',
        name: 'Redeem Offer - Dedicated Email',
        products: offerIds,
        refId,
        properties: { evar6: '' },
      },
      112: {
        events: 'event112',
        name: 'SMS Opened',
        products: offerIds,
        refId: 'sms',
        properties: { evar6: '' },
      },
      114: {
        events: 'event114',
        name: 'Client Logo Click',
        products: offerIds,
        refId,
        properties: { evar6: '' },
      },
    };

    const refIds = {
      '10031hdr-lbl': events['110'],
      '10033hdr-lbl': events['110'],
      '10031redeem': events['111'],
      '10033redeem': events['111'],
      sms: events['112'],
      '10031logo': events['114'],
      '10033logo': events['114'],
    };

    const payload = refIds[refId] || {};

    if (payload && payload.events) {

     return post(
        `${this.api}/mgo-api/api/v1/e/${this.coaUUID ?? parsedUUID}`,
        { ...payload },
        {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        },
      );
    }

    return Promise.resolve(payload && payload.events)
  }

  /**
   * Track continuation to confirmation page
   * @param {('EMAIL'|'PRINT'|'SMS')} type - event type
   * @param {Array} offerIds - ids of offers to track
   * @param {Boolean} getAll - all offers selected
   * @return {Promise}
   */
  async trackContinueToConfirm(type = 'EMAIL', offerIds, getAll = false) {
    await this._getAuthToken();
    if (!this._isAuthenticated()) {
      return notAuthenticatedErrorPromise;
    }

    const coaUUIDUrl = new URL(window.location)?.pathname;
    const parsedUUID = coaUUIDUrl?.split('/')?.pop(); 

    const response = await post(
      `${this.api}/mgo-api/api/v1/e/${this.coaUUID? this.coaUUID : parsedUUID}`,
      {
        event: 'event105',
        properties: {
          evar1: 'ICOA',
          evar6: '',
          prop2: '',
          evar4: type,
          evar25: getAll ? 'GET_ALL' : 'GET_SELECTED',
        },
      },
      {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      },
    );
    sessionStorage.removeItem('coaUUID');
    return response;
  }
}
